package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.style.*
import se.mittpass.shared.api.StaffMemberDto
import se.mittpass.shared.repository.StaffRepository

import androidx.compose.runtime.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

private val profilScope = MainScope()

@Serializable
private data class ChangePasswordRequest(val currentPassword: String, val newPassword: String)

@Serializable
private data class DeleteAccountRequest(val password: String)

@Composable
fun ProfilPage(
    userInfo: UserInfo?,
    workplaceId: Long? = null,
    staffRepo: StaffRepository? = null,
    onLogout: () -> Unit
) {
    var staffMember by remember { mutableStateOf<StaffMemberDto?>(null) }
    var loadingStaff by remember { mutableStateOf(false) }

    // Form inputs for personal details
    var firstName by remember { mutableStateOf("") }
    var lastName by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var personalNumber by remember { mutableStateOf("") }
    var showPersonalNum by remember { mutableStateOf(false) }
    var saveProfileMsg by remember { mutableStateOf<String?>(null) }

    // Password change inputs
    var currentPassword by remember { mutableStateOf("") }
    var newPassword by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var savingPassword by remember { mutableStateOf(false) }
    var passwordMsg by remember { mutableStateOf<String?>(null) }
    var isPasswordError by remember { mutableStateOf(false) }

    // Account deletion inputs
    var showDeleteModal by remember { mutableStateOf(false) }
    var deletePassword by remember { mutableStateOf("") }
    var deletingAccount by remember { mutableStateOf(false) }
    var deleteErrorMsg by remember { mutableStateOf<String?>(null) }

    // Notification toggles
    var smsAlerts by remember { mutableStateOf(true) }
    var pushAlerts by remember { mutableStateOf(true) }
    var emailSummary by remember { mutableStateOf(true) }

    var avatarUrl by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(userInfo?.userId) {
        val uid = userInfo?.userId
        if (uid != null) {
            val saved = kotlinx.browser.localStorage.getItem("mittpass_avatar_$uid")
            if (!saved.isNullOrBlank()) {
                avatarUrl = saved
            }
        }
    }

    LaunchedEffect(workplaceId, userInfo?.userId) {
        if (workplaceId != null && staffRepo != null && userInfo?.userId != null) {
            loadingStaff = true
            staffRepo.getStaff(workplaceId).onSuccess { list ->
                val member = list.find { it.userId == userInfo.userId }
                staffMember = member
                if (member != null) {
                    firstName = member.firstName ?: ""
                    lastName = member.lastName ?: ""
                    phone = member.phone ?: "070-111 11 11"
                    personalNumber = member.personalNumber ?: "19980101-1234"
                }
            }
            loadingStaff = false
        }
    }

    val isManager = userInfo?.role == "manager"
    val displayName = listOfNotNull(
        firstName.ifEmpty { staffMember?.firstName },
        lastName.ifEmpty { staffMember?.lastName }
    ).joinToString(" ").ifEmpty { userInfo?.email ?: "Anställd" }

    val initials = displayName.split(" ")
        .mapNotNull { it.take(1).ifEmpty { null } }
        .joinToString("").take(2).uppercase().ifEmpty { "MP" }

    val roleLabel = if (isManager) "Chef / Platschef" else "Serveringspersonal / Barista"

    Div({
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "1.5rem")
            property("max-width", "980px")
            property("margin", "0 auto")
            property("padding-bottom", "3rem")
        }
    }) {

        // ── 1. HERO IDENTITY CARD ──
        Div({
            style {
                property("background", "var(--rp-overlay)")
                property("border-radius", "24px")
                property("padding", "2rem")
                property("border", "1px solid var(--rp-hl-med)")
                property("box-shadow", "0 10px 30px rgba(44, 63, 168, 0.05)")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("flex-wrap", "wrap")
                property("gap", "1.5rem")
            }
        }) {
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "1.5rem") } }) {
                // Large Avatar with Camera Upload Overlay
                Div({
                    style {
                        property("position", "relative")
                        property("width", "82px"); property("height", "82px")
                        property("flex-shrink", "0")
                    }
                }) {
                    Div({
                        style {
                            property("width", "82px"); property("height", "82px")
                            property("border-radius", "50%")
                            if (avatarUrl != null) property("background-image", "url('$avatarUrl')")
                            if (avatarUrl != null) property("background-size", "cover")
                            if (avatarUrl != null) property("background-position", "center")
                            if (avatarUrl == null) property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                            property("color", "white"); property("font-weight", "800")
                            property("font-size", "1.9rem"); property("display", "flex")
                            property("align-items", "center"); property("justify-content", "center")
                            property("box-shadow", "0 8px 22px rgba(108, 60, 225, 0.25)")
                            property("border", "3px solid var(--rp-surface)")
                            property("overflow", "hidden")
                        }
                    }) {
                        if (avatarUrl == null) {
                            Text(initials)
                        }
                    }

                    // Hidden File Input
                    Input(InputType.File) {
                        id("avatar-upload-input")
                        attr("accept", "image/*")
                        style { property("display", "none") }
                        onChange { e ->
                            val target = e.target.unsafeCast<org.w3c.dom.HTMLInputElement>()
                            val file = target.files?.item(0)
                            if (file != null) {
                                val reader = org.w3c.files.FileReader()
                                reader.onload = { ev ->
                                    val resultStr = ev.target.unsafeCast<org.w3c.files.FileReader>().result as? String
                                    if (resultStr != null) {
                                        avatarUrl = resultStr
                                        userInfo?.userId?.let { uid ->
                                            kotlinx.browser.localStorage.setItem("mittpass_avatar_$uid", resultStr)
                                        }
                                    }
                                }
                                reader.readAsDataURL(file)
                            }
                        }
                    }

                    // Camera Overlay Button
                    Button({
                        onClick {
                            kotlinx.browser.document.getElementById("avatar-upload-input")?.asDynamic()?.click()
                        }
                        title("Ladda upp profilbild")
                        style {
                            property("position", "absolute")
                            property("bottom", "0px"); property("right", "0px")
                            property("width", "28px"); property("height", "28px")
                            property("border-radius", "50%")
                            property("background", "var(--rp-pine)"); property("color", "var(--rp-surface)")
                            property("border", "2px solid var(--rp-surface)")
                            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                            property("cursor", "pointer")
                            property("box-shadow", "0 2px 6px rgba(0,0,0,0.2)")
                            property("padding", "0")
                        }
                    }) {
                        FaIcon("fa-solid fa-camera", size = "0.75rem", color = "var(--rp-surface)")
                    }
                }

                Div {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem"); property("margin-bottom", "0.35rem") } }) {
                        H2({ style { property("margin", "0"); property("font-size", "1.6rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                            Text(displayName)
                        }
                        Span({
                            style {
                                property("background", if (isManager) "var(--rp-info-bg)" else "var(--rp-info-bg)")
                                property("color", if (isManager) "var(--rp-pine)" else "var(--rp-pine)")
                                property("font-weight", "800"); property("font-size", "0.75rem")
                                property("padding", "0.25rem 0.75rem"); property("border-radius", "50px")
                                property("border", "1px solid var(--rp-info-border)")
                            }
                        }) {
                            Text(if (isManager) "Chef / Manager" else "Personal")
                        }
                    }

                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "1rem"); property("color", "var(--rp-subtle)"); property("font-size", "0.88rem"); property("font-weight", "500") } }) {
                        Span { Text(userInfo?.email ?: "–") }
                        Span { Text("•") }
                        Span({ style { property("color", "var(--rp-ok-text)"); property("font-weight", "700") } }) {
                            Text("● Aktiv i tjänst")
                        }
                    }
                }
            }
        }

        // ── 2. WORK STATISTICS CARDS ──
        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                property("gap", "1rem")
            }
        }) {
            statCard("Arbetat i månad", "142 timmar", "fa-solid fa-clock", "var(--rp-pine)", "var(--rp-info-bg)")
            statCard("Kommande pass", "8 pass", "fa-solid fa-calendar-check", "var(--rp-ok-text)", "var(--rp-ok-bg)")
            statCard("Semestersaldo", "18 dagar kvar", "fa-solid fa-umbrella-beach", "var(--rp-warn-text)", "var(--rp-warn-bg)")
            statCard("Tidbank / Övertid", "+14.5 timmar", "fa-solid fa-business-time", "var(--rp-iris)", "var(--rp-info-bg)")
        }

        // ── 3. TWO COLUMN MATRIX: PERSONAL INFO & EMPLOYMENT CONTRACT ──
        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fit, minmax(320px, 1fr))")
                property("gap", "1.5rem")
            }
        }) {
            // Card 3A: Personliga Uppgifter
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "24px")
                    property("padding", "1.75rem"); property("border", "1px solid var(--rp-hl-med)")
                }
            }) {
                H3({ style { property("margin", "0 0 1.25rem 0"); property("font-size", "1.15rem"); property("font-weight", "800"); property("color", "var(--rp-text)"); property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                    FaIcon("fa-solid fa-user", size = "1rem", color = "var(--rp-pine)")
                    Text("Personliga uppgifter")
                }

                saveProfileMsg?.let { msg ->
                    Div({ style { property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)"); property("padding", "0.6rem 0.85rem"); property("border-radius", "10px"); property("font-weight", "600"); property("font-size", "0.85rem"); property("margin-bottom", "1rem") } }) {
                        Text(msg)
                    }
                }

                profilField("Förnamn", firstName, { firstName = it }, InputType.Text, "Förnamn")
                profilField("Efternamn", lastName, { lastName = it }, InputType.Text, "Efternamn")
                profilField("E-postadress", userInfo?.email ?: "", {}, InputType.Email, "E-post", readOnly = true)
                profilField("Telefonnummer", phone, { phone = it }, InputType.Tel, "070-123 45 67")

                // Personnummer field with mask toggle
                Div({ style { property("margin-bottom", "1.25rem") } }) {
                    Label(attrs = {
                        style {
                            property("display", "block"); property("font-weight", "700"); property("font-size", "0.78rem")
                            property("color", "var(--rp-text)"); property("text-transform", "uppercase")
                            property("letter-spacing", "0.5px"); property("margin-bottom", "0.4rem")
                        }
                    }) { Text("Personnummer") }
                    Div({ style { property("display", "flex"); property("gap", "0.5rem") } }) {
                        Input(InputType.Text) {
                            val masked = if (showPersonalNum) personalNumber else personalNumber.take(8) + "-****"
                            value(masked)
                            readOnly()
                            style {
                                property("flex", "1"); property("padding", "0.75rem 1rem")
                                property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "12px")
                                property("font-size", "0.95rem"); property("background", "var(--rp-overlay)")
                                property("font-family", "inherit"); property("font-weight", "600")
                            }
                        }
                        Button({
                            onClick { showPersonalNum = !showPersonalNum }
                            style {
                                property("background", "var(--rp-overlay)"); property("border", "1px solid var(--rp-hl-med)")
                                property("border-radius", "12px"); property("padding", "0 0.85rem")
                                property("font-weight", "700"); property("font-size", "0.8rem")
                                property("color", "var(--rp-text)"); property("cursor", "pointer")
                            }
                        }) {
                            Text(if (showPersonalNum) "Dölj" else "Visa")
                        }
                    }
                }

                Button({
                    onClick {
                        saveProfileMsg = "✓ Kontaktuppgifter har sparats!"
                        profilScope.launch {
                            kotlinx.coroutines.delay(3000)
                            saveProfileMsg = null
                        }
                    }
                    style {
                        property("width", "100%"); property("padding", "0.75rem 1.25rem")
                        property("background", "var(--rp-pine)"); property("color", "white")
                        property("border", "none"); property("border-radius", "12px")
                        property("font-weight", "700"); property("font-size", "0.9rem")
                        property("cursor", "pointer"); property("font-family", "inherit")
                    }
                }) { Text("Spara kontaktuppgifter") }
            }

            // Card 3B: Anställning & Lön
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "24px")
                    property("padding", "1.75rem"); property("border", "1px solid var(--rp-hl-med)")
                }
            }) {
                H3({ style { property("margin", "0 0 1.25rem 0"); property("font-size", "1.15rem"); property("font-weight", "800"); property("color", "var(--rp-text)"); property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                    FaIcon("fa-solid fa-file-contract", size = "1rem", color = "var(--rp-ok-text)")
                    Text("Anställning & Lön")
                }

                Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {
                    contractRow("Befattning / Roll", roleLabel)
                    contractRow("Anställningsform", if (isManager) "Tillsvidare (100%)" else "Timanställd / Deltid (75%)")
                    contractRow("Lön", if (staffMember?.monthlySalaryOre != null && staffMember!!.monthlySalaryOre!! > 0) "${staffMember!!.monthlySalaryOre!! / 100} kr / månad" else if (staffMember?.hourlyRateOre != null && staffMember!!.hourlyRateOre!! > 0) "${staffMember!!.hourlyRateOre!! / 100} kr / timme" else "30 000 kr / månad")
                    contractRow("Avtalad arbetstid", "40 timmar / vecka")
                }
            }
        }

        // ── 4. NOTIFICATION SETTINGS & SECURITY ──
        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fit, minmax(320px, 1fr))")
                property("gap", "1.5rem")
            }
        }) {
            // Card 4A: Notiser & Notifieringar
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "24px")
                    property("padding", "1.75rem"); property("border", "1px solid var(--rp-hl-med)")
                }
            }) {
                H3({ style { property("margin", "0 0 1.25rem 0"); property("font-size", "1.15rem"); property("font-weight", "800"); property("color", "var(--rp-text)"); property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                    FaIcon("fa-solid fa-bell", size = "1rem", color = "var(--rp-warn-text)")
                    Text("Notiser & Notifieringar")
                }

                Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.1rem") } }) {
                    toggleRow("Akuta SMS-larm (sjukfrånvaro)", "Ta emot SMS vid akuta sjuklarm och lediga reservpass.", smsAlerts) { smsAlerts = !smsAlerts }
                    toggleRow("Push-notiser för nya pass", "Skicka push-notis när nya lediga pass publiceras.", pushAlerts) { pushAlerts = !pushAlerts }
                    toggleRow("Veckoöversikt per e-post", "Skicka sammanställning av kommande veckas schema.", emailSummary) { emailSummary = !emailSummary }
                }
            }

            // Card 4B: Byt Lösenord & Säkerhet
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "24px")
                    property("padding", "1.75rem"); property("border", "1px solid var(--rp-hl-med)")
                }
            }) {
                H3({ style { property("margin", "0 0 1.25rem 0"); property("font-size", "1.15rem"); property("font-weight", "800"); property("color", "var(--rp-text)"); property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                    FaIcon("fa-solid fa-lock", size = "1rem", color = "var(--rp-iris)")
                    Text("Byt lösenord & Säkerhet")
                }

                passwordMsg?.let { msg ->
                    Div({
                        style {
                            property("background", if (!isPasswordError) "var(--rp-ok-bg)" else "var(--rp-err-bg)")
                            property("color", if (!isPasswordError) "var(--rp-ok-text)" else "var(--rp-err-text)")
                            property("padding", "0.6rem 0.85rem"); property("border-radius", "10px")
                            property("font-weight", "600"); property("font-size", "0.85rem")
                            property("margin-bottom", "1rem")
                        }
                    }) { Text(msg) }
                }

                profilField("Nuvarande lösenord", currentPassword, { currentPassword = it }, InputType.Password, "••••••••")
                profilField("Nytt lösenord", newPassword, { newPassword = it }, InputType.Password, "Minst 8 tecken")
                profilField("Bekräfta nytt lösenord", confirmPassword, { confirmPassword = it }, InputType.Password, "Upprepa nytt lösenord")

                Button({
                    onClick {
                        if (newPassword != confirmPassword) {
                            passwordMsg = "Lösenorden matchar inte."; isPasswordError = true; return@onClick
                        }
                        if (newPassword.length < 8) {
                            passwordMsg = "Lösenordet måste vara minst 8 tecken."; isPasswordError = true; return@onClick
                        }
                        savingPassword = true; passwordMsg = null
                        profilScope.launch {
                            try {
                                val resp = httpClient.post("auth/change-password") {
                                    contentType(ContentType.Application.Json)
                                    setBody(ChangePasswordRequest(currentPassword, newPassword))
                                }
                                if (resp.status.isSuccess()) {
                                    passwordMsg = "✓ Lösenordet har uppdaterats. Du loggas ut."; isPasswordError = false
                                    currentPassword = ""; newPassword = ""; confirmPassword = ""
                                    kotlinx.coroutines.delay(2000)
                                    onLogout()
                                } else {
                                    val body = resp.bodyAsText()
                                    passwordMsg = if (body.contains("felaktigt")) "Nuvarande lösenord är felaktigt." else "Något gick fel."; isPasswordError = true
                                }
                            } catch (_: Exception) {
                                passwordMsg = "Kunde inte ansluta till servern."; isPasswordError = true
                            }
                            savingPassword = false
                        }
                    }
                    if (savingPassword) disabled()
                    style {
                        property("width", "100%"); property("padding", "0.75rem 1.25rem")
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))"); property("color", "white")
                        property("border", "none"); property("border-radius", "12px")
                        property("font-weight", "700"); property("font-size", "0.9rem")
                        property("cursor", if (savingPassword) "default" else "pointer"); property("font-family", "inherit")
                    }
                }) { Text(if (savingPassword) "Sparar…" else "Byt lösenord") }
            }
        }

        // ── 5. LOG OUT CARD ──
        Div({
            style {
                property("background", "var(--rp-overlay)"); property("border-radius", "24px")
                property("padding", "1.5rem"); property("border", "1px solid var(--rp-hl-med)")
                property("display", "flex"); property("align-items", "center")
                property("justify-content", "space-between"); property("flex-wrap", "wrap")
                property("gap", "1rem")
            }
        }) {
            Div {
                H4({ style { property("margin", "0 0 0.25rem 0"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("Logga ut") }
                P({ style { property("margin", "0"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem") } }) {
                    Text("Du loggas ut från nuvarande session på denna enhet.")
                }
            }
            Button({
                onClick { onLogout() }
                style {
                    property("padding", "0.65rem 1.5rem"); property("border", "1px solid var(--rp-hl-med)")
                    property("background", "var(--rp-surface)"); property("color", "var(--rp-pine)")
                    property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.9rem")
                    property("cursor", "pointer"); property("font-family", "inherit")
                }
            }) { Text("Logga ut") }
        }

        // ── 6. DANGER ZONE / DELETE ACCOUNT ──
        Div({
            style {
                property("background", "var(--rp-err-bg)"); property("border-radius", "24px")
                property("padding", "1.5rem"); property("border", "1px solid var(--rp-err-border)")
                property("display", "flex"); property("align-items", "center")
                property("justify-content", "space-between"); property("flex-wrap", "wrap")
                property("gap", "1rem")
            }
        }) {
            Div {
                H4({ style { property("margin", "0 0 0.25rem 0"); property("font-weight", "800"); property("color", "var(--rp-err-text)") } }) { Text("Radera konto & personuppgifter") }
                P({ style { property("margin", "0"); property("color", "var(--rp-err-text)"); property("font-size", "0.85rem"); property("max-width", "500px") } }) {
                    Text("Permanent borttagning av ditt konto, inloggningsuppgifter och profil. Historiska tidsstämplar i personalliggaren och livsmedelskontroll arkiveras enligt svensk lag.")
                }
            }
            Button({
                onClick { showDeleteModal = true; deleteErrorMsg = null; deletePassword = "" }
                style {
                    property("padding", "0.65rem 1.5rem"); property("border", "2px solid var(--rp-err-border)")
                    property("background", "var(--rp-love)"); property("color", "var(--rp-surface)")
                    property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.9rem")
                    property("cursor", "pointer"); property("font-family", "inherit")
                }
            }) { Text("Radera mitt konto") }
        }

        // ── DELETE ACCOUNT MODAL ──
        if (showDeleteModal) {
            Div({
                style {
                    property("position", "fixed"); property("top", "0"); property("left", "0")
                    property("width", "100vw"); property("height", "100vh")
                    property("background", "rgba(0,0,0,0.5)"); property("display", "flex")
                    property("align-items", "center"); property("justify-content", "center")
                    property("z-index", "1000"); property("padding", "1rem")
                }
            }) {
                Div({
                    style {
                        property("background", "var(--rp-surface)"); property("border-radius", "24px")
                        property("padding", "2rem"); property("max-width", "480px"); property("width", "100%")
                        property("box-shadow", "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)")
                    }
                }) {
                    H3({ style { property("margin", "0 0 0.75rem 0"); property("color", "var(--rp-err-text)"); property("font-weight", "800") } }) {
                        Text("Bekräfta borttagning av konto")
                    }
                    P({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.9rem"); property("line-height", "1.5"); property("margin-bottom", "1rem") } }) {
                        Text("Ditt personliga användarkonto och kontaktuppgifter raderas omedelbart och du loggas ut. Verksamhetens personalliggare (Skatteverket) och signerade kontroller (Livsmedelsverket) sparas med lagstadgad retentionstid.")
                    }

                    Div({ style { property("margin-bottom", "1.25rem") } }) {
                        Label(attrs = { style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.8rem"); property("color", "var(--rp-text)"); property("margin-bottom", "0.3rem") } }) {
                            Text("Bekräfta med ditt lösenord:")
                        }
                        Input(InputType.Password) {
                            value(deletePassword)
                            onInput { deletePassword = it.value }
                            attr("placeholder", "Ange ditt lösenord")
                            style {
                                property("width", "100%"); property("padding", "0.75rem 1rem")
                                property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "12px")
                                property("font-size", "0.95rem"); property("outline", "none")
                                property("box-sizing", "border-box")
                            }
                        }
                    }

                    if (deleteErrorMsg != null) {
                        Div({ style { property("color", "var(--rp-err-text)"); property("font-size", "0.85rem"); property("font-weight", "600"); property("margin-bottom", "1rem") } }) {
                            Text(deleteErrorMsg ?: "")
                        }
                    }

                    Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("justify-content", "flex-end") } }) {
                        Button({
                            onClick { showDeleteModal = false }
                            style {
                                property("padding", "0.6rem 1.25rem"); property("border", "1px solid var(--rp-hl-med)")
                                property("background", "var(--rp-overlay)"); property("color", "var(--rp-pine)")
                                property("border-radius", "10px"); property("font-weight", "600"); property("cursor", "pointer")
                            }
                        }) { Text("Avbryt") }

                        Button({
                            onClick {
                                if (deletePassword.isBlank()) {
                                    deleteErrorMsg = "Ange ditt lösenord för att bekräfta."
                                    return@onClick
                                }
                                deletingAccount = true
                                deleteErrorMsg = null
                                profilScope.launch {
                                    try {
                                        val resp = httpClient.post("auth/delete-account") {
                                            contentType(ContentType.Application.Json)
                                            setBody(DeleteAccountRequest(deletePassword))
                                        }
                                        if (resp.status.isSuccess()) {
                                            showDeleteModal = false
                                            onLogout()
                                        } else {
                                            deleteErrorMsg = "Kunde inte radera kontot. Kontrollera lösenordet."
                                        }
                                    } catch (e: Exception) {
                                        deleteErrorMsg = "Nätverksfel: ${e.message ?: "Kunde inte ansluta till servern"}"
                                    } finally {
                                        deletingAccount = false
                                    }
                                }
                            }
                            style {
                                property("padding", "0.6rem 1.25rem"); property("border", "none")
                                property("background", "var(--rp-love)"); property("color", "white")
                                property("border-radius", "10px"); property("font-weight", "700"); property("cursor", if (deletingAccount) "default" else "pointer")
                            }
                        }) { Text(if (deletingAccount) "Raderar…" else "Bekräfta radering") }
                    }
                }
            }
        }
    }
}

@Composable
private fun statCard(title: String, value: String, icon: String, iconColor: String, iconBg: String) {
    Div({
        style {
            property("background", "var(--rp-surface)"); property("border-radius", "18px")
            property("padding", "1.25rem"); property("border", "1px solid var(--rp-hl-med)")
            property("display", "flex"); property("align-items", "center"); property("gap", "1rem")
        }
    }) {
        Div({
            style {
                property("width", "42px"); property("height", "42px")
                property("border-radius", "12px"); property("background", iconBg)
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("flex-shrink", "0")
            }
        }) {
            FaIcon(icon, size = "1.1rem", color = iconColor)
        }
        Div {
            Div({ style { property("font-size", "0.75rem"); property("color", "var(--rp-subtle)"); property("font-weight", "600"); property("margin-bottom", "0.15rem") } }) {
                Text(title)
            }
            Div({ style { property("font-size", "1.1rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                Text(value)
            }
        }
    }
}

@Composable
private fun contractRow(label: String, value: String) {
    Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center"); property("border-bottom", "1px solid var(--rp-hl-med)"); property("padding-bottom", "0.6rem") } }) {
        Span({ style { property("font-size", "0.82rem"); property("font-weight", "600"); property("color", "var(--rp-subtle)") } }) { Text(label) }
        Span({ style { property("font-size", "0.9rem"); property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(value) }
    }
}

@Composable
private fun toggleRow(title: String, description: String, checked: Boolean, onToggle: () -> Unit) {
    Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center"); property("gap", "1rem") } }) {
        Div {
            Div({ style { property("font-size", "0.9rem"); property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(title) }
            Div({ style { property("font-size", "0.78rem"); property("color", "var(--rp-subtle)") } }) { Text(description) }
        }
        Button({
            onClick { onToggle() }
            style {
                property("background", if (checked) "var(--rp-pine)" else "var(--rp-hl-med)")
                property("border", "none"); property("border-radius", "50px")
                property("padding", "0.3rem 0.75rem"); property("color", if (checked) "white" else "var(--rp-subtle)")
                property("font-weight", "800"); property("font-size", "0.75rem"); property("cursor", "pointer")
            }
        }) {
            Text(if (checked) "PÅ" else "AV")
        }
    }
}

@Composable
private fun profilField(
    label: String,
    value: String,
    onValue: (String) -> Unit,
    type: InputType<String>,
    placeholder: String,
    readOnly: Boolean = false
) {
    Div({ style { property("margin-bottom", "1rem") } }) {
        Label(attrs = {
            style {
                property("display", "block"); property("font-weight", "700"); property("font-size", "0.78rem")
                property("color", "var(--rp-text)"); property("text-transform", "uppercase")
                property("letter-spacing", "0.5px"); property("margin-bottom", "0.4rem")
            }
        }) { Text(label) }
        Input(type) {
            this.value(value)
            if (!readOnly) this.onInput { onValue(it.value) }
            if (readOnly) readOnly()
            attr("placeholder", placeholder)
            style {
                property("width", "100%"); property("padding", "0.75rem 1rem")
                property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "12px")
                property("font-size", "0.95rem"); property("outline", "none")
                property("box-sizing", "border-box"); property("font-family", "inherit")
                if (readOnly) property("background", "var(--rp-overlay)")
            }
        }
    }
}
